import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { Student } from "@/services/db/types";
import { format } from "date-fns";
import { toast } from "sonner";

// SET PROGRAMS PER PAGE TO 2 AS REQUESTED
const PROGRAMS_PER_PAGE = 2;

/**
 * Export student enrollment details as JPG - ULTRA-FAST WITH PAGINATION
 */
export const printStudentEnrollmentAsJPG = async (student: Student, startPage = 1, endPage?: number) => {
  try {
    const loadingToast = toast.loading('Generating student enrollment JPG...');

    const { content, filename, styles } = generateStudentEnrollmentContentPaginated(student, startPage, endPage);
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as JPG with ultra-fast settings
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student enrollment');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student enrollment downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading student enrollment:', error);
    toast.error('Failed to download student enrollment');
  }
};

/**
 * Export student enrollment details as PDF - ULTRA-FAST WITH PAGINATION
 */
export const printStudentEnrollmentAsPDF = async (student: Student, startPage = 1, endPage?: number) => {
  try {
    const loadingToast = toast.loading('Generating student enrollment PDF...');

    const { content, filename, styles } = generateStudentEnrollmentContentPaginated(student, startPage, endPage);
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as PDF with ultra-fast settings
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student enrollment');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student enrollment downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading student enrollment:', error);
    toast.error('Failed to download student enrollment');
  }
};

/**
 * Legacy function - now calls JPG version for backward compatibility
 */
export const printStudentEnrollment = async (student: Student) => {
  await printStudentEnrollmentAsJPG(student);
};

/**
 * Generate paginated content for both JPG and PDF downloads
 */
const generateStudentEnrollmentContentPaginated = (student: Student, startPage = 1, endPage?: number) => {
  // Get full student name including surname
  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;

  const programs = student.programs || [];
  const totalPrograms = programs.length;
  const totalPages = Math.ceil(totalPrograms / PROGRAMS_PER_PAGE);
  
  const actualEndPage = endPage || totalPages;
  const actualStartPage = Math.max(1, Math.min(startPage, totalPages));  
  const finalEndPage = Math.max(actualStartPage, Math.min(actualEndPage, totalPages));

  let allPages = '';

  for (let page = actualStartPage; page <= finalEndPage; page++) {
    const startIndex = (page - 1) * PROGRAMS_PER_PAGE;
    const endIndex = Math.min(startIndex + PROGRAMS_PER_PAGE, totalPrograms);
    const pagePrograms = programs.slice(startIndex, endIndex);

    const pageContent = `
      <div class="print-page">
        ${getHeaderHtml(`Student Enrollment Details - Page ${page} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="enrollment-container">
            <div class="student-header">
              <div class="student-name">${fullName}</div>
              <div class="student-id">Student ID: ${student.id}</div>
              <div class="enrollment-date">Enrolled on: ${student.createdAt ? format(new Date(student.createdAt), 'PPP') : 'N/A'}</div>
              <div class="page-info">Page ${page} of ${totalPages} | Programs ${startIndex + 1} - ${endIndex} of ${totalPrograms}</div>
            </div>
            
            <div class="enrollment-details">
              <div class="section">
                <div class="section-title">Personal Information</div>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">Full Name:</span>
                    <span class="detail-value">${fullName}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Father's Name:</span>
                    <span class="detail-value">${student.fathersName || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Age:</span>
                    <span class="detail-value">${student.age || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Contact:</span>
                    <span class="detail-value">${student.contactNumber || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Email:</span>
                    <span class="detail-value">${student.email || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Address:</span>
                    <span class="detail-value">${student.address || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div class="section">
                <div class="section-title">Program Enrollment Details (${startIndex + 1} - ${endIndex})</div>
                <div class="programs-summary">
                  ${pagePrograms.length > 0 
                    ? `
                      <div class="programs-list">
                        ${pagePrograms.map(program => `
                          <div class="program-enrollment-item">
                            <div class="program-name">${program.title}</div>
                            <div class="program-details">
                              <span class="program-category">${program.category}</span>
                              <span class="program-fee">AFN ${program.fee || 0}</span>
                              <span class="program-status status-${program.feeStatus}">${program.feeStatus || 'pending'}</span>
                            </div>
                            <div class="program-dates">
                              Enrolled: ${program.enrolledAt ? format(new Date(program.enrolledAt), 'PP') : 'N/A'} | 
                              Completion: ${program.completionDate ? format(new Date(program.completionDate), 'PP') : 'N/A'}
                            </div>
                          </div>
                        `).join('')}
                      </div>
                    `
                    : '<div class="no-programs">No programs on this page</div>'
                  }
                </div>
              </div>
            </div>
          </div>
          
          ${getSignatureHtml('student')}
        </div>
        
        ${getFooterHtml('student')}
      </div>
    `;

    allPages += pageContent;
    if (page < finalEndPage) {
      allPages += '<div class="page-break"></div>';
    }
  }

  const styles = `
    ${getCommonPrintStyles()}
    .enrollment-container {
      margin-top: 20px;
    }
    .student-header {
      text-align: center;
      margin-bottom: 30px;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 8px;
    }
    .student-name {
      font-size: 28px;
      font-weight: bold;
      color: #3D7069;
      margin-bottom: 8px;
    }
    .student-id {
      font-size: 16px;
      color: #666;
      margin-bottom: 5px;
    }
    .enrollment-date {
      font-size: 14px;
      color: #888;
      font-style: italic;
    }
    .page-info {
      font-size: 12px;
      color: #888;
      margin-top: 5px;
      font-style: italic;
    }
    .enrollment-details {
      margin-top: 20px;
    }
    .section {
      margin-bottom: 25px;
      padding: 20px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
    }
    .section-title {
      font-size: 18px;
      font-weight: bold;
      color: #3D7069;
      margin-bottom: 15px;
      border-bottom: 2px solid #3D7069;
      padding-bottom: 5px;
    }
    .detail-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
    .detail-item {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #f0f0f0;
    }
    .detail-label {
      font-weight: 500;
      color: #555;
    }
    .detail-value {
      color: #333;
    }
    .programs-list {
      margin-top: 15px;
    }
    .program-enrollment-item {
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 12px;
      margin-bottom: 10px;
    }
    .program-name {
      font-size: 16px;
      font-weight: bold;
      color: #333;
      margin-bottom: 5px;
    }
    .program-details {
      display: flex;
      gap: 15px;
      margin-bottom: 5px;
    }
    .program-category {
      background: #e3f2fd;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      color: #1976d2;
    }
    .program-fee {
      font-weight: 500;
      color: #333;
    }
    .program-dates {
      font-size: 12px;
      color: #666;
    }
    .status-paid {
      color: #2e7d32;
      font-weight: 500;
    }
    .status-partial {
      color: #ed6c02;
      font-weight: 500;
    }
    .status-pending {
      color: #d32f2f;
      font-weight: 500;
    }
    .no-programs {
      text-align: center;
      color: #666;
      font-style: italic;
      padding: 20px;
    }
    .header-logo {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
  `;

  // Create filename
  const pageRange = actualStartPage === finalEndPage ? `Page_${actualStartPage}` : `Pages_${actualStartPage}-${finalEndPage}`;
  const filename = `Student_Enrollment_${fullName.replace(/\s+/g, '_')}_${pageRange}_${new Date().toISOString().split('T')[0]}`;

  return { 
    content: allPages, 
    filename, 
    styles 
  };
};
